package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.data.dto.FootballCompetitionDto
import com.arena_web.data.dto.FootballMatchDto
import com.arena_web.data.dto.FootballStandingsDto
import com.arena_web.data.dto.NewsArticleDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.sections.news.NewsContentDesktop
import com.arena_web.ui.sections.news.NewsContentMobile
import com.arena_web.ui.sections.news.NewsFeedSort
import com.arena_web.ui.sections.news.NewsMatchesContentDesktop
import com.arena_web.ui.sections.news.NewsMatchesContentMobile
import com.arena_web.ui.sections.news.NewsModeToggle
import com.arena_web.ui.sections.news.NewsPageMode
import com.arena_web.ui.sections.news.StandingsTable
import com.arena_web.ui.utils.isMobile
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.core.Page
import kotlinx.coroutines.launch

private const val NEWS_FEED_PAGE_SIZE = 30

/** Sports the match center serves (backend `/v1/sports/{sport}`). */
private val MATCH_SPORTS = setOf("football", "basketball", "tennis", "volleyball")

/** Loaded feed pages carried across SPA navigation to /news/{id} and back —
 *  reopening the feed restores the same articles (and, via
 *  NewsFeedScrollMemory, the same scroll offset) instead of resetting to
 *  page 1 at the top. In-memory only; a full reload starts fresh. */
private object NewsFeedSession {
    var key: String = ""
    var articles: List<NewsArticleDto> = emptyList()
    var page: Int = 1
    var hasMore: Boolean = false
}

// Arena News (design spec: docs/superpowers/specs/2026-09-14-arena-news-design.md).
// Two modes switched by the floating right rail:
//  - News: "Today's news" carousel + "All news" list (sort: new | popular),
//    fed by /v1/news (published articles only).
//  - Matches ("Leagues & tournaments"): day-strip calendar + competition chips
//    + match list + standings, fed by the /v1/sports/{sport} proxy. The sport
//    follows the page's global sport selector (football fallback).
@Page("/news")
@Composable
fun NewsPage() {
    val pageCtx = com.varabyte.kobweb.core.rememberPageContext()
    val mobile = isMobile()
    val container = LocalAppContainer.current
    val repository = container.newsRepository

    // Mode survives navigation and reload — reopening /news lands on
    // whatever the user used last (news feed or the match center).
    var mode by remember {
        mutableStateOf(
            if (Storage.get(StorageKeys.NEWS_PAGE_MODE) == "matches") NewsPageMode.Matches else NewsPageMode.News
        )
    }
    val switchMode: (NewsPageMode) -> Unit = {
        mode = it
        Storage.save(StorageKeys.NEWS_PAGE_MODE, if (it == NewsPageMode.Matches) "matches" else "news")
    }
    var sportReload by remember { mutableStateOf(0) }

    // ── PRO gate (match-detail clicks + feed banner) ──
    // Guests hit the sign-in gate first; signed-in non-subscribers get the
    // news-variant paywall. Repositories are null while logged out, so the
    // subscription repo doubles as the auth check (People-page pattern).
    val isGuest = container.subscriptionRepository == null
    val isSubscribed by container.isSubscribed.collectAsState()
    val subscriptionState by container.subscription.collectAsState()
    LaunchedEffect(Unit) { if (!isGuest) container.ensureSubscriptionLoaded() }
    var showAuthGate by remember { mutableStateOf(false) }
    var paywallSource by remember { mutableStateOf<String?>(null) }
    // For signed-in users wait for the subscription to load before showing the
    // banner — otherwise it flashes at every PRO user on page open.
    val showProBanner = isGuest || (subscriptionState != null && !isSubscribed)
    val onProBannerClick: () -> Unit = {
        if (isGuest) showAuthGate = true
        else if (!isSubscribed) paywallSource = "news_feed_banner"
    }
    fun gatedMatchClick(action: () -> Unit) {
        when {
            isGuest -> showAuthGate = true
            !isSubscribed -> paywallSource = "match_detail"
            else -> action()
        }
    }

    // ── News mode state ──
    var sort by remember { mutableStateOf(NewsFeedSort.NEW) }
    // Hashtag filter — seeded from ?tag= (article-detail chips link here).
    var selectedTag by remember {
        mutableStateOf(
            org.w3c.dom.url.URLSearchParams(kotlinx.browser.window.location.search).get("tag")?.takeIf { it.isNotBlank() }
        )
    }
    var todayArticles by remember { mutableStateOf<List<NewsArticleDto>>(emptyList()) }
    var feedArticles by remember { mutableStateOf<List<NewsArticleDto>>(emptyList()) }
    // Infinite scroll over /v1/news pages (same pattern as the Feed page).
    var feedPage by remember { mutableStateOf(1) }
    var feedHasMore by remember { mutableStateOf(false) }
    var feedLoadingMore by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    // ── Matches mode state ──
    var selectedDate by remember { mutableStateOf(todayLocalDate()) }
    // Multi-select league filter (checkbox sheet); empty = all competitions.
    var selectedCompetitions by remember { mutableStateOf<Set<String>>(emptySet()) }
    var competitions by remember { mutableStateOf<List<FootballCompetitionDto>>(emptyList()) }
    var matches by remember { mutableStateOf<List<FootballMatchDto>>(emptyList()) }
    var standings by remember { mutableStateOf<FootballStandingsDto?>(null) }
    var standingsFor by remember { mutableStateOf<String?>(null) }
    var standingsClick by remember { mutableStateOf(0) }

    fun selectedSportParam(): String? =
        Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase()?.takeIf { it.isNotBlank() && it != "all" }

    // Matches mode follows the same global sport selector as the news feed;
    // sports without match-center data ("Other", All) fall back to football.
    // Padel is the exception (user call 21.09.2026): no provider carries it
    // (TheSportsDB has no padel), so instead of silently showing football
    // fixtures the match center renders its empty state.
    // Standings are football-only for now: TheSportsDB's v1 API has no tables
    // for NBA/EuroLeague/volleyball (verified 2026-09-20) and tennis tours
    // have none by nature — hide the affordances there.
    val padelSelected = remember(sportReload) { selectedSportParam() == "padel" }
    val matchSport = remember(sportReload) { selectedSportParam()?.takeIf { it in MATCH_SPORTS } ?: "football" }
    val standingsAvailable = matchSport == "football" && !padelSelected

    // Client UI language — the backend serves translated copy for it.
    fun uiLocale(): String? = Storage.get(StorageKeys.LANGUAGE)?.takeIf { it.isNotBlank() && it != "en" }

    // "Most discussed" carousel — the manually curated featured strip
    // (admin toggles articles into it); refetch on sport change.
    LaunchedEffect(sportReload) {
        val params = buildMap {
            put("featured", "true")
            put("size", "10")
            selectedSportParam()?.let { put("sport", it) }
            uiLocale()?.let { put("locale", it) }
        }
        repository.getNews(params).onSuccess { todayArticles = it.items }
    }

    fun feedParams(page: Int): Map<String, String> = buildMap {
        put("page", "$page")
        put("size", "$NEWS_FEED_PAGE_SIZE")
        put("sort", if (sort == NewsFeedSort.POPULAR) "popular" else "new")
        selectedSportParam()?.let { put("sport", it) }
        selectedTag?.let { put("tag", it) }
        uiLocale()?.let { put("locale", it) }
    }

    fun sessionKey(): String =
        listOf(sort.name, selectedSportParam().orEmpty(), selectedTag.orEmpty(), uiLocale().orEmpty())
            .joinToString("|")

    // Main feed — refetch on sort or sport change. Favorites needs the
    // hashtag-favorites backend (spec §6) — until then it's an empty list,
    // the section renders its empty state.
    LaunchedEffect(sort, sportReload, selectedTag) {
        if (sort == NewsFeedSort.FAVORITES) {
            feedArticles = emptyList()
            feedHasMore = false
            return@LaunchedEffect
        }
        // Coming back from an article: reopen the exact pages the user had
        // and let the content scroller restore the saved offset.
        val key = sessionKey()
        if (feedArticles.isEmpty() && NewsFeedSession.key == key && NewsFeedSession.articles.isNotEmpty()) {
            feedArticles = NewsFeedSession.articles
            feedPage = NewsFeedSession.page
            feedHasMore = NewsFeedSession.hasMore
            com.arena_web.ui.sections.news.NewsFeedScrollMemory.pendingRestore =
                com.arena_web.ui.sections.news.NewsFeedScrollMemory.lastScrollTop
            return@LaunchedEffect
        }
        repository.getNews(feedParams(1)).onSuccess {
            feedArticles = it.items
            feedPage = 1
            feedHasMore = it.page < it.totalPages
            NewsFeedSession.key = key
            NewsFeedSession.articles = it.items
            NewsFeedSession.page = 1
            NewsFeedSession.hasMore = feedHasMore
        }
    }

    // Appends the next feed page — wired to the content scrollers' near-bottom
    // trigger. distinctBy guards against shift duplicates when new articles
    // are published between page fetches.
    val loadMoreFeed: () -> Unit = {
        if (!feedLoadingMore && feedHasMore && sort != NewsFeedSort.FAVORITES) {
            feedLoadingMore = true
            scope.launch {
                val next = feedPage + 1
                repository.getNews(feedParams(next)).onSuccess {
                    feedArticles = (feedArticles + it.items).distinctBy { a -> a.id }
                    feedPage = next
                    feedHasMore = it.page < it.totalPages
                    NewsFeedSession.key = sessionKey()
                    NewsFeedSession.articles = feedArticles
                    NewsFeedSession.page = next
                    NewsFeedSession.hasMore = feedHasMore
                }
                feedLoadingMore = false
            }
        }
    }

    // Competitions — static per sport; the league filter resets on sport
    // change (codes from another sport would silently empty the list).
    LaunchedEffect(matchSport, padelSelected) {
        selectedCompetitions = emptySet()
        standingsFor = null
        if (padelSelected) {
            competitions = emptyList()
            return@LaunchedEffect
        }
        repository.getCompetitions(matchSport).onSuccess { competitions = it }
    }

    // Matches — refetch on day / league-filter change, then keep polling
    // every 60s while the Matches mode stays open so live scores tick without
    // a manual refresh. The backend caches provider responses (60s TTL), so
    // polling clients never exceed the provider rate limit.
    LaunchedEffect(mode, selectedDate, selectedCompetitions, matchSport, padelSelected) {
        if (mode != NewsPageMode.Matches) return@LaunchedEffect
        if (padelSelected) {
            matches = emptyList()
            return@LaunchedEffect
        }
        val competitionParam = selectedCompetitions.takeIf { it.isNotEmpty() }?.joinToString(",")
        while (true) {
            var count = 0
            repository.getMatches(matchSport, selectedDate, competitionParam).onSuccess {
                matches = it
                count = it.size
            }
            // A cold date fills league-by-league as the backend's rate budget
            // allows — poll quickly until something arrives, then settle into
            // the one-minute live cadence.
            kotlinx.coroutines.delay(if (count == 0) 10_000L else 60_000L)
        }
    }

    // Empty-day shortcut: when the selected day has no matches, surface the
    // nearest upcoming matchday so the user can jump there in one tap.
    var nextMatchday by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(mode, selectedDate, matches, matchSport, padelSelected) {
        nextMatchday = null
        if (mode != NewsPageMode.Matches || matches.isNotEmpty() || padelSelected) return@LaunchedEffect
        repository.getNextMatchday(matchSport, selectedDate).onSuccess { nextMatchday = it }
    }

    // Standings sheet/popup, opened from a competition badge or the header
    // button. The click nonce keys the effect so REPEAT clicks on the same
    // league refetch too — otherwise one failed attempt (backend restart,
    // transient network) would leave the button dead until a page reload.
    LaunchedEffect(standingsFor, standingsClick) {
        standings = null
        val code = standingsFor ?: return@LaunchedEffect
        repository.getStandings(matchSport, code).onSuccess { standings = it }
    }
    val openStandings: (String) -> Unit = { code ->
        if (standingsAvailable) {
            standingsFor = code
            standingsClick++
        }
    }

    // The feed shows EVERYTHING (today included); the curated strip may
    // duplicate feed items — that's by design.
    val dedupedFeed = feedArticles

    AppPageLayout(
        activeTab = SidebarTab.News,
        title = "News",
        mobileReady = true,
        onSportChanged = { sportReload++ },
        mobileShowThemeButton = true,
    ) {
        if (mobile) {
            when (mode) {
                NewsPageMode.News -> NewsContentMobile(
                    // The switcher sits opposite the "Today's news" header —
                    // the top bar keeps its stock bell / auth buttons.
                    modeToggle = { NewsModeToggle(mode, switchMode, mobile = true) },
                    todayArticles = todayArticles,
                    feedArticles = dedupedFeed,
                    sort = sort,
                    onSortChange = { sort = it },
                    activeTag = selectedTag,
                    onClearTag = { selectedTag = null },
                    onLoadMore = loadMoreFeed,
                    showProBanner = showProBanner,
                    onProBannerClick = onProBannerClick,
                )
                NewsPageMode.Matches -> NewsMatchesContentMobile(
                    modeToggle = { NewsModeToggle(mode, switchMode, mobile = true) },
                    competitions = competitions,
                    matches = matches,
                    selectedDate = selectedDate,
                    onDateSelected = { selectedDate = it },
                    selectedCompetitions = selectedCompetitions,
                    onCompetitionsChanged = { selectedCompetitions = it },
                    onOpenStandings = openStandings,
                    nextMatchday = nextMatchday,
                    showStandings = standingsAvailable,
                    onMatchClick = { match ->
                        gatedMatchClick { com.arena_web.ui.sections.news.openMatchPage(pageCtx, matchSport, match) }
                    },
                )
            }
        } else {
            // Desktop: the news↔matches switcher sits right of the section
            // title inside each mode's content ("Most discussed" / "Matches"),
            // same pattern as mobile.
            org.jetbrains.compose.web.dom.Div(
                attrs = {
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("width", "100%")
                        property("flex-grow", "1")
                        property("min-height", "0")
                    }
                }
            ) {
                when (mode) {
                    NewsPageMode.News -> NewsContentDesktop(
                        modeToggle = { NewsModeToggle(mode, switchMode, mobile = false) },
                        todayArticles = todayArticles,
                        feedArticles = dedupedFeed,
                        sort = sort,
                        onSortChange = { sort = it },
                        activeTag = selectedTag,
                        onClearTag = { selectedTag = null },
                        onLoadMore = loadMoreFeed,
                        showProBanner = showProBanner,
                        onProBannerClick = onProBannerClick,
                    )
                    NewsPageMode.Matches -> NewsMatchesContentDesktop(
                        modeToggle = { NewsModeToggle(mode, switchMode, mobile = false) },
                        competitions = competitions,
                        matches = matches,
                        selectedDate = selectedDate,
                        onDateSelected = { selectedDate = it },
                        selectedCompetitions = selectedCompetitions,
                        onCompetitionsChanged = { selectedCompetitions = it },
                        onOpenStandings = openStandings,
                        nextMatchday = nextMatchday,
                        showStandings = standingsAvailable,
                        onMatchClick = { match ->
                            gatedMatchClick { com.arena_web.ui.sections.news.openMatchPage(pageCtx, matchSport, match) }
                        },
                    )
                }
            }
        }

        // PRO-gate overlays: sign-in first for guests, then the news-variant
        // paywall for signed-in non-subscribers.
        com.arena_web.ui.components.overlay.AccountRequiredModal(
            isOpen = showAuthGate,
            onClose = { showAuthGate = false },
            onSignedIn = {
                showAuthGate = false
                kotlinx.browser.window.location.reload()
            },
        )
        com.arena_web.ui.components.overlay.SubscribeModal(
            isOpen = paywallSource != null,
            onClose = { paywallSource = null },
            source = paywallSource ?: "news",
            variant = com.arena_web.ui.components.overlay.PaywallVariant.News,
        )

        // Standings overlay — sheet on phones, centred popup on desktop.
        // The SHEET's visibility follows standingsFor (not the loaded table):
        // switching leagues in place clears the body but keeps the sheet
        // mounted at full height, so nothing jumps.
        if (mobile && standingsFor != null) {
            val sheetCompetition = competitions.firstOrNull { it.code == standingsFor }
            com.arena_web.ui.components.overlay.BottomSheet(
                onDismiss = { standingsFor = null },
                fullHeight = true,
            ) {
                // Sticky title: emblem + league name, next-league arrow at the
                // right — stays put while the table scrolls under it.
                com.arena_web.ui.sections.news.StandingsSheetHeader(
                    competition = sheetCompetition,
                    onNextLeague = {
                        val list = competitions
                        if (list.isNotEmpty()) {
                            val idx = list.indexOfFirst { it.code == standingsFor }
                            standingsFor = list[(idx + 1).mod(list.size)].code
                            standingsClick++
                        }
                    },
                )
                // The sheet panel is not a scroll container — a full league
                // table overflows it invisibly. Own scroller + trailing
                // spacer (padding-bottom is dropped at scroll-end).
                org.jetbrains.compose.web.dom.Div(
                    attrs = {
                        style {
                            property("width", "100%")
                            property("flex-grow", "1")
                            property("min-height", "0")
                            property("overflow-y", "auto")
                            property("scrollbar-width", "none")
                        }
                    }
                ) {
                    standings?.let { table ->
                        StandingsTable(table, mobile = true, showTitle = false)
                    }
                    org.jetbrains.compose.web.dom.Div(
                        attrs = {
                            style {
                                property("width", "100%")
                                property("height", "24px")
                                property("flex", "0 0 24px")
                            }
                        }
                    )
                }
            }
        }
        if (!mobile && standingsFor != null) {
            val sheetCompetition = competitions.firstOrNull { it.code == standingsFor }
            com.arena_web.ui.components.overlay.DesktopPopup(
                onDismiss = { standingsFor = null },
                widthPx = 640,
            ) {
                // Pinned title row: emblem + name, next-league arrow at the
                // right; the table scrolls in its own container below, so the
                // header never leaves the screen and switching leagues swaps
                // the body in place (popup never jumps).
                org.jetbrains.compose.web.dom.Div(
                    attrs = {
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "space-between")
                            property("width", "100%")
                            property("padding-bottom", "12px")
                        }
                    }
                ) {
                    org.jetbrains.compose.web.dom.Div(
                        attrs = {
                            style {
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "10px")
                            }
                        }
                    ) {
                        val emblem = sheetCompetition?.emblem.orEmpty()
                        if (emblem.isNotBlank()) {
                            org.jetbrains.compose.web.dom.Img(
                                src = emblem,
                                attrs = {
                                    style {
                                        property("width", "28px")
                                        property("height", "28px")
                                        property("object-fit", "contain")
                                        com.arena_web.ui.sections.news.leagueEmblemFilter(sheetCompetition?.code)
                                            ?.let { property("filter", it) }
                                    }
                                }
                            )
                        }
                        org.jetbrains.compose.web.dom.Span(
                            attrs = {
                                style {
                                    property("font-size", "20px")
                                    property("font-weight", "600")
                                    property("color", "var(--arena-text-1)")
                                }
                            }
                        ) { org.jetbrains.compose.web.dom.Text(sheetCompetition?.name.orEmpty()) }
                    }
                    com.arena_web.ui.sections.news.StandingsNextLeagueButton {
                        val list = competitions
                        if (list.isNotEmpty()) {
                            val idx = list.indexOfFirst { it.code == standingsFor }
                            standingsFor = list[(idx + 1).mod(list.size)].code
                            standingsClick++
                        }
                    }
                }
                org.jetbrains.compose.web.dom.Div(
                    attrs = {
                        style {
                            property("width", "100%")
                            property("max-height", "64vh")
                            property("overflow-y", "auto")
                            property("scrollbar-width", "none")
                        }
                    }
                ) {
                    standings?.let { table -> StandingsTable(table, mobile = false, showTitle = false) }
                }
            }
        }
    }
}

/** Local yyyy-MM-dd — the backend filters published_at / kickoff by UTC day,
 *  local is close enough for a "today" strip. */
private fun todayLocalDate(): String {
    val d = kotlin.js.Date()
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "${d.getFullYear()}-$m-$day"
}
